import { useLayoutEffect, useRef, useEffect, useState } from "react";
import Comment from "./Comment";
import { CommentModel } from "@/types";
import { usePlayer } from "@/lib/PlayerContext";
import { BsPinAngleFill } from "react-icons/bs";

// URLをリンクとして表示するヘルパー関数
const linkifyMessage = (text: string) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  
  return parts.map((part, index) => {
    if (part.match(urlRegex)) {
      return (
        <a 
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="tw-text-blue-300 tw-underline hover:tw-text-blue-200"
          onClick={(e) => e.stopPropagation()}
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

export interface CommentAreaProps {
  fullScreen: boolean;
}

const CommentArea = ({ fullScreen }: CommentAreaProps) => {
  const { comments, commentScrollEnabled, pinnedComment, showComment } = usePlayer();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [prevCommentCount, setPrevCommentCount] = useState(0);
  
  const scrollBottom = (smooth = true) => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: smooth ? "smooth" : "instant",
      });
    }
  };
  
  // コメント数の変化を監視
  useEffect(() => {
    const currentCommentCount = comments.length;
    
    if (currentCommentCount !== prevCommentCount) {
      // コメント数が変化した場合
      if (commentScrollEnabled) {
        // スクロール機能が有効な場合はスムーズにスクロール
        setTimeout(() => {
          scrollBottom(true);
        }, 50);
      } else {
        // 従来の動作
        setTimeout(() => {
          scrollBottom(false);
        }, 100);
      }
      setPrevCommentCount(currentCommentCount);
    }
  }, [comments, commentScrollEnabled, prevCommentCount]);
  
  // フルスクリーン切り替え時の調整
  useLayoutEffect(() => {
    setTimeout(() => {
      scrollBottom(false);
    }, 200);
  }, [fullScreen]);
  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
      <div className="tw-h-full tw-w-full tw-flex tw-flex-col tw-font-sans">
        {/* 通常コメント一覧（上部に押し上げられる） */}
        <div
          ref={scrollContainerRef}
          className="tw-flex-1 tw-w-full tw-overflow-y-scroll tw-overflow-x-hidden tw-hidden-scrollbar tw-pointer-events-auto"
          style={{
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
            touchAction: 'pan-y'
          }}
        >
          {comments.map((comment, index) => {
            const isNewComment = commentScrollEnabled && index >= prevCommentCount - 1;
            return (
              <div
                className={`tw-py-2 ${isNewComment ? 'tw-animate-fade-in' : ''}`}
                key={`${comment.name}-${comment.createdAt}-${index}`}
                style={{
                  animation: isNewComment ? 'fadeIn 0.3s ease-in-out' : undefined
                }}
              >
                <Comment {...comment}></Comment>
              </div>
            );
          })}
        </div>
        
        {/* ピン留めコメント表示エリア（下部に表示） */}
        {pinnedComment && showComment && comments.length > 0 && (
          <div 
            className="tw-flex-shrink-0 tw-mt-1.5 tw-bg-amber-500/30 tw-backdrop-blur-sm tw-rounded-lg tw-p-1.5 tw-border tw-border-amber-400/50 tw-pointer-events-auto"
            style={{
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
            }}
          >
            <div className="tw-flex tw-items-center tw-gap-1 tw-mb-0.5">
              <BsPinAngleFill className="tw-text-amber-300 tw-text-sm" />
              <span 
                className="tw-text-white tw-text-xs tw-font-bold"
                style={{ 
                  textShadow: '1px 1px 2px rgba(64, 64, 64, 0.7), -1px -1px 2px rgba(64, 64, 64, 0.7)' 
                }}
              >
                {pinnedComment.name}
              </span>
            </div>
            <div
              className="tw-px-1.5 tw-py-0.5 tw-whitespace-pre-wrap tw-text-sm tw-text-left tw-text-white"
              style={{ 
                textShadow: '1px 1px 2px rgba(64, 64, 64, 0.7), -1px -1px 2px rgba(64, 64, 64, 0.7)' 
              }}
            >
              {linkifyMessage(pinnedComment.message)}
            </div>
          </div>
        )}
      </div>
    </>
  );
};
export default CommentArea;
